import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { NAV } from "./SidebarNav";
import { canSee } from "../lib/roles";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");
const isActive = (item, pathname) => item.children
  ? item.children.some((c) => (c.end ? pathname === c.to : pathname.startsWith(c.to)))
  : (item.end ? pathname === item.to : pathname.startsWith(item.to));

function Sheet({ group, onClose }) {
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} data-testid="mobile-sheet-overlay" />
      <div className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-line bg-surface pb-[max(16px,env(safe-area-inset-bottom))] shadow-2xl fade-up">
        <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-[#3a3a3c]" />
        <div className="flex items-center justify-between px-5 pt-3 pb-1">
          <span className="text-lg font-semibold text-slate-50">{group.label}</span>
          <button onClick={onClose} aria-label="Chiudi" data-testid="mobile-sheet-close" className="flex h-11 w-11 items-center justify-center rounded-md text-slate-400"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-3 pb-2">
          {group.children.map((c) => {
            const Icon = c.icon;
            return (
              <NavLink key={c.to} to={c.to} end={c.end} onClick={onClose} data-testid={`mobile-nav-${slug(c.label)}`}
                className={({ isActive: a }) => `flex h-12 items-center gap-3 rounded-md px-3 text-base ${a ? "bg-gold-soft text-slate-50" : "text-slate-200"}`}>
                <Icon className="h-5 w-5 text-slate-400" /> {c.label}
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function MobileNav({ user, modules }) {
  const { pathname } = useLocation();
  const [sheet, setSheet] = useState(null);
  const mods = modules || { sales: true, marketing: true, secretary: true };
  const groupModule = { Vendite: "sales", Marketing: "marketing", Segreteria: "secretary" };
  const items = NAV
    .filter((it) => ["Dashboard", "Hermes AI", "Vendite", "Marketing", "Segreteria"].includes(it.label))
    .filter((it) => !groupModule[it.label] || mods[groupModule[it.label]] !== false)
    .map((it) => (it.children ? { ...it, children: it.children.filter((c) => (!c.onlyIfNoMarketing || mods.marketing === false) && canSee(user, c.to)) } : it))
    .filter((it) => (it.children ? it.children.length > 0 : canSee(user, it.to)));
  const label = (l) => (l === "Hermes AI" ? "Hermes" : l);
  return (
    <>
      <nav data-testid="mobile-nav" aria-label="Navigazione principale"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden">
        {items.map((it) => {
          const Icon = it.icon;
          const active = isActive(it, pathname);
          const cls = `flex min-h-14 flex-1 flex-col items-center justify-center gap-1 px-1 pt-2 pb-1 text-xs font-medium ${active ? "text-gold" : "text-slate-400"}`;
          return it.children ? (
            <button key={it.label} onClick={() => setSheet(it)} data-testid={`mobile-nav-${slug(it.label)}`} className={cls} aria-haspopup="dialog">
              <Icon className="h-5 w-5" />{label(it.label)}
            </button>
          ) : (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={`mobile-nav-${slug(it.label)}`} className={cls}>
              <Icon className="h-5 w-5" />{label(it.label)}
            </NavLink>
          );
        })}
      </nav>
      {sheet && <Sheet group={sheet} onClose={() => setSheet(null)} />}
    </>
  );
}
